import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import SearchIcon from '@mui/icons-material/Search'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_DIVIDER_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import TerritoryCountryStats from './TerritoryCountryStats'
import StatusChip, { MEMBERSHIP_STATUS_CHIP } from '../../component/StatusChip'
import { buildListSearch as buildListSearchQuery, createInitialListState } from '../../utils/listPageState'
import { formatLocalDateTime, renderDateTime } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'
import MembershipSupportTools from './MembershipSupportTools'
import { formatMembershipPlanPrice, getMembershipDisplayStatus } from './membershipDisplay'

const DEFAULT_SEARCH_SELECT = {
    searchStatus: '',
    searchStore: '',
    searchPlanType: '',
}

const SEARCH_SELECT_PARAMS = {
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
    },
    searchStore: {
        param: 'store',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStore,
    },
    searchPlanType: {
        param: 'plan',
        defaultValue: DEFAULT_SEARCH_SELECT.searchPlanType,
    },
}

const OPERATION_SETTING_FIELDS = [
    {
        field: 'purchaseEnabled',
        label: '구매 검증',
    },
    {
        field: 'restoreEnabled',
        label: '구매 복원',
    },
    {
        field: 'webhookProcessingEnabled',
        label: 'Webhook 처리',
    },
    {
        field: 'storeResyncEnabled',
        label: '상태 재동기화',
    },
    {
        field: 'territoryBenefitsEnabled',
        label: 'Territory 혜택',
    },
    {
        field: 'couponIssueEnabled',
        label: '쿠폰 발급',
    },
]

const DEFAULT_OPERATION_SETTINGS = OPERATION_SETTING_FIELDS.reduce((result, item) => {
    result[item.field] = false
    return result
}, {})

const buildOperationDraft = (settings = {}) => OPERATION_SETTING_FIELDS.reduce((result, item) => {
    result[item.field] = Boolean(settings[item.field]?.enabled)
    return result
}, { ...DEFAULT_OPERATION_SETTINGS })

const getOperationSourceLabel = (source = '') => {
    if (source === 'db') return 'DB'
    if (source === 'error') return 'ERROR'
    return 'DEFAULT'
}

export default function Memberships() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    }))
    const initialListState = initialListStateRef.current
    const silentListRefreshRef = useRef(false)
    const [loading, setLoading] = useState(true)
    // 'list' | 'territoryStats'
    const [activeTab, setActiveTab] = useState('list')
    const [totalCount, setTotalCount] = useState(0)
    // 결제 통화 기준 국내/해외 구성 (서버가 목록과 같은 필터로 집계해 준다)
    const [currencySummary, setCurrencySummary] = useState({
        domesticCount: 0,
        overseasCount: 0,
        unknownCount: 0,
    })
    const [rows, setRows] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const [operationLoading, setOperationLoading] = useState(true)
    const [operationSaving, setOperationSaving] = useState(false)
    const [operationSettings, setOperationSettings] = useState({})
    const [operationDraftSettings, setOperationDraftSettings] = useState(DEFAULT_OPERATION_SETTINGS)
    const [operationErrorMessage, setOperationErrorMessage] = useState('')

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/membership/getList', {
                page: curPage,
                offset,
                searchText: searchQuery,
                searchSelect,
            })

            if (data.code === 200) {
                setTotalCount(Number(data.data?.count || 0))
                setCurrencySummary({
                    domesticCount: Number(data.data?.currencySummary?.domesticCount || 0),
                    overseasCount: Number(data.data?.currencySummary?.overseasCount || 0),
                    unknownCount: Number(data.data?.currencySummary?.unknownCount || 0),
                })
                setRows(data.data?.rows || [])
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                const errorMessage = data.message || '멤버십 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setRows([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '멤버십 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setRows([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    const fetchOperationSettings = useCallback(async () => {
        setOperationLoading(true)
        setOperationErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/membership/getOperationSettings', {})

            if (data.code === 200) {
                const nextSettings = data.data?.settings || {}
                setOperationSettings(nextSettings)
                setOperationDraftSettings(buildOperationDraft(nextSettings))
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                const errorMessage = data.message || '멤버십 운영 설정을 불러오지 못했습니다.'
                setOperationErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '멤버십 운영 설정을 불러오지 못했습니다.'
            setOperationErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setOperationLoading(false)
        }
    }, [history])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        fetchOperationSettings()
    }, [fetchOperationSettings])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
            searchSelect,
            selectParams: SEARCH_SELECT_PARAMS,
        })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setCurPage(1)
        setSearchQuery((searchText || '').trim())
        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value,
        })
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    const searchButtonClick = () => {
        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
        }
    }

    const updateOperationDraftSetting = (field) => (event) => {
        setOperationDraftSettings((prevSettings) => ({
            ...prevSettings,
            [field]: event.target.checked,
        }))
    }

    const resetOperationDraftSettings = () => {
        setOperationDraftSettings(buildOperationDraft(operationSettings))
    }

    const isOperationDirty = OPERATION_SETTING_FIELDS.some((item) => {
        return operationDraftSettings[item.field] !== Boolean(operationSettings[item.field]?.enabled)
    })

    const saveOperationSettings = async () => {
        if (!isOperationDirty || operationSaving) return
        if (!window.confirm('멤버십 운영 스위치를 저장하시겠습니까?')) return

        setOperationSaving(true)
        setOperationErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/membership/updateOperationSettings', {
                settings: operationDraftSettings,
                reason: 'admin_membership_operation_settings',
            })

            if (data.code === 200) {
                const nextSettings = data.data?.settings || {}
                setOperationSettings(nextSettings)
                setOperationDraftSettings(buildOperationDraft(nextSettings))
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('멤버십 운영 설정이 저장되었습니다.')
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                const errorMessage = data.message || '멤버십 운영 설정을 저장하지 못했습니다.'
                setOperationErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '멤버십 운영 설정을 저장하지 못했습니다.'
            setOperationErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setOperationSaving(false)
        }
    }

    const hasActiveFilters = Boolean((searchQuery || '').trim())
        || searchSelect.searchStatus !== DEFAULT_SEARCH_SELECT.searchStatus
        || searchSelect.searchStore !== DEFAULT_SEARCH_SELECT.searchStore
        || searchSelect.searchPlanType !== DEFAULT_SEARCH_SELECT.searchPlanType

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="멤버십 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 멤버십 구독이 없습니다."
                    description="회원/토큰 검색어와 상태, 스토어, 상품 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="멤버십 구독 내역이 없습니다."
                description="구독 내역이 생성되면 이 목록에 표시됩니다."
            />
        )
    }

    if (loading) return <Spiner />

    if (activeTab === 'territoryStats') {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>멤버십 구독</Title>
                    <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)} sx={{ mb: 2 }}>
                        <Tab label="구독 목록" value="list" />
                        <Tab label="영토 생성 국가" value="territoryStats" />
                    </Tabs>
                    <TerritoryCountryStats />
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>멤버십 구독</Title>
                <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)} sx={{ mb: 2 }}>
                    <Tab label="구독 목록" value="list" />
                    <Tab label="영토 생성 국가" value="territoryStats" />
                </Tabs>
                <Box sx={{ borderTop: 1, borderBottom: 1, borderColor: 'divider', py: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="subtitle1" component="h2">운영 스위치</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button size="small" variant="outlined" onClick={fetchOperationSettings} disabled={operationLoading || operationSaving}>새로고침</Button>
                            <Button size="small" variant="outlined" onClick={resetOperationDraftSettings} disabled={!isOperationDirty || operationSaving}>되돌리기</Button>
                            <Button size="small" variant="contained" onClick={saveOperationSettings} disabled={!isOperationDirty || operationSaving || operationLoading}>저장</Button>
                        </Box>
                    </Box>
                    <Grid container spacing={2}>
                        {OPERATION_SETTING_FIELDS.map((item) => (
                            <Grid item xs={12} sm={4} key={item.field}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={operationDraftSettings[item.field]}
                                                onChange={updateOperationDraftSetting(item.field)}
                                                disabled={operationLoading || operationSaving}
                                            />
                                        }
                                        label={item.label}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {getOperationSourceLabel(operationSettings[item.field]?.source)}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                    {operationErrorMessage && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {operationErrorMessage}
                        </Typography>
                    )}
                </Box>
                <MembershipSupportTools />
                <ListFilterBar>
                    <ListFilterForm onSubmit={handleSearchSubmit}>
                        <TextField
                            select
                            size="small"
                            label="상태"
                            name="searchStatus"
                            value={searchSelect.searchStatus}
                            onChange={updateSearchSelect}
                            sx={LIST_FILTER_SELECT_FIELD_SX}
                        >
                            <MenuItem value="">전체 상태</MenuItem>
                            <MenuItem value="trial">무료체험</MenuItem>
                            <MenuItem value="active">활성</MenuItem>
                            <MenuItem value="cancel_scheduled">해지예정</MenuItem>
                            <MenuItem value="grace">유예</MenuItem>
                            <MenuItem value="expired">만료</MenuItem>
                            <MenuItem value="refunded">환불</MenuItem>
                            <MenuItem value="revoked">회수</MenuItem>
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="스토어"
                            name="searchStore"
                            value={searchSelect.searchStore}
                            onChange={updateSearchSelect}
                            sx={LIST_FILTER_SELECT_FIELD_SX}
                        >
                            <MenuItem value="">전체 스토어</MenuItem>
                            <MenuItem value="google">Google</MenuItem>
                            <MenuItem value="apple">Apple</MenuItem>
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="상품"
                            name="searchPlanType"
                            value={searchSelect.searchPlanType}
                            onChange={updateSearchSelect}
                            sx={LIST_FILTER_SELECT_FIELD_SX}
                        >
                            <MenuItem value="">전체 상품</MenuItem>
                            <MenuItem value="monthly">월간</MenuItem>
                            <MenuItem value="yearly">연간</MenuItem>
                        </TextField>
                        <Divider orientation="vertical" flexItem sx={LIST_FILTER_DIVIDER_SX} />
                        <InputBase
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="회원/토큰 검색"
                            sx={LIST_FILTER_SEARCH_FIELD_SX}
                        />
                        <IconButton type="submit" size="small">
                            <SearchIcon />
                        </IconButton>
                        {hasActiveFilters && <ResetFilterButton onClick={resetFilters} />}
                    </ListFilterForm>
                    {/* 결제 통화 기준 구성. 필터를 걸면 그 결과 기준으로 다시 집계된다. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: { sm: 'auto' } }}>
                        <Chip size="small" variant="outlined" label={`국내 ${formatNumber(currencySummary.domesticCount)}명`} />
                        <Chip size="small" variant="outlined" color="info" label={`해외 ${formatNumber(currencySummary.overseasCount)}명`} />
                        {currencySummary.unknownCount > 0 && (
                            <Tooltip title="스토어에서 결제 통화를 받지 못한 구독입니다. 무료체험처럼 결제 이력이 없으면 여기에 포함됩니다.">
                                <Chip size="small" variant="outlined" color="warning" label={`미확인 ${formatNumber(currencySummary.unknownCount)}명`} />
                            </Tooltip>
                        )}
                    </Box>
                </ListFilterBar>
                <ListTableContainer>
                <Table size="small" sx={{ minWidth: 1080 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>No</TableCell>
                            <TableCell>회원</TableCell>
                            <TableCell>상태</TableCell>
                            <TableCell>스토어</TableCell>
                            <TableCell>상품</TableCell>
                            <TableCell>상품 기준가(원화)</TableCell>
                            <TableCell>시작일</TableCell>
                            <TableCell>혜택 종료</TableCell>
                            <TableCell>원장 갱신</TableCell>
                            <TableCell>자동갱신</TableCell>
                            <TableCell align="right">관리</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={11}>
                                    {renderEmptyState()}
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map((row, index) => (
                            <TableRow key={row.membershipId} hover>
                                <TableCell>{row.rownum || totalCount - ((curPage - 1) * offset) - index}</TableCell>
                                <TableCell>
                                    {row.nickName || row.username || '-'}
                                    <br />
                                    {row.userId || row.email || '-'}
                                </TableCell>
                                <TableCell><StatusChip status={getMembershipDisplayStatus(row)} config={MEMBERSHIP_STATUS_CHIP} /></TableCell>
                                <TableCell>{row.store || '-'}</TableCell>
                                <TableCell>{row.planType === 'monthly' ? '월간' : row.planType === 'yearly' ? '연간' : '-'}</TableCell>
                                <TableCell>{formatMembershipPlanPrice(row.priceKrw)}</TableCell>
                                <TableCell>{renderDateTime(formatLocalDateTime(row.startedAt))}</TableCell>
                                <TableCell>{renderDateTime(row.benefitUntil)}</TableCell>
                                <TableCell>{renderDateTime(row.ledgerUpdatedAt || row.latestPurchaseRecordedAt)}</TableCell>
                                <TableCell>{row.autoRenewStatus === 1 ? 'ON' : row.autoRenewStatus === 0 ? 'OFF' : '-'}</TableCell>
                                <TableCell align="right">
                                    <ListActionGroup>
                                        <EditIcon path="membership" id={row.membershipId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect} />
                                    </ListActionGroup>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                </ListTableContainer>
                <Pagination curPage={curPage} setCurPage={setCurPage} totalCount={totalCount} offset={offset} />
            </Paper>
            <Snackbars open={openSnackbar} onClose={handleClose} severity={severitySnackbar} message={messageSnackbar} />
        </Grid>
    )
}
